"use client";

import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router";

import { isValidCheckoutId, seedCheckoutId } from "@/lib/checkout-id";
import { logWarn } from "@/services/logging/logger";

import { WelcomeScreen } from "../views/welcome-screen";
import { collectHandshakeMetadata } from "../lib/collect-metadata";
import { buildAuthLoginUrl } from "../lib/build-auth-login-url";
import { emitCheckoutHandshake } from "../analytics";

const WELCOME_DURATION_MS = 3000;

/**
 * Invalid handshake entries still land on the existing PPUI not-found route
 * under `/v2` (handshake itself is served at site-root `/handshake`).
 */
const PLAN_SELECTION_BASENAME = "/v2";

export function HandshakePage() {
  const [searchParams] = useSearchParams();
  const checkoutId = searchParams.get("checkoutId") ?? undefined;
  // Guards the one-time event emit only. Dwell is intentionally restarted on
  // every effect run so React StrictMode's remount stays safe.
  const emittedRef = useRef(false);

  useEffect(() => {
    if (!isValidCheckoutId(checkoutId)) {
      logWarn("handshake", "Invalid or missing checkoutId in handshake query", {
        checkoutId,
      });
      window.location.replace(`${PLAN_SELECTION_BASENAME}/not-found`);
      return;
    }

    // Query is canonical on handshake entry. Seed sessionStorage so the rest
    // of the app (and Auth's post-login redirect_url) share the same id.
    seedCheckoutId(checkoutId);

    if (!emittedRef.current) {
      emittedRef.current = true;
      emitCheckoutHandshake(checkoutId, collectHandshakeMetadata());
    }

    // Always send customers through Tamara Auth after the welcome dwell —
    // ignore any leftover checkout cookie/token so every new session logs in.
    let cancelled = false;
    const timerId = window.setTimeout(() => {
      if (cancelled) return;
      window.location.assign(
        buildAuthLoginUrl({
          checkoutId,
          search: window.location.search,
        }),
      );
    }, WELCOME_DURATION_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [checkoutId]);

  return (
    <div data-testid="handshake-mounted">
      <WelcomeScreen />
    </div>
  );
}
